import { useCallback, useEffect, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { selectAccountSlug, selectCampaignId } from '@/redux/session/selectors';
import { trackCampaignLaunched, trackCampaignLaunchFailed } from '@/segment/segmentCampaignLaunch';
import { Campaign } from '@/shared/types/campaigns';

import { useCampaigns } from './useCampaigns';
import { useCampaignSenders } from './useCampaignSenders';
import {
  selectDraftCampaignName,
  selectDraftSubscribers,
  selectDraftAiAgentData,
} from '../redux/selectors';
import {
  getAudienceStats,
  parseCampaignSendersChain,
  getUserTimezone,
  calculateScheduledStartTime as calculateScheduledTime,
  restoreScheduleFromCampaign,
  isScheduledTimeInFuture,
} from '../utils';

/**
 * Form data interface for the agentic review scheduling (start + end times)
 */
export interface AgenticReviewSchedulingFormData {
  startScheduleType: 'immediately' | 'custom';
  startDate: Date | undefined;
  startTime: string;
  timezone: string;
  doNotEnd: boolean;
  endDate: Date | undefined;
  endTime: string;
}

/**
 * Hook that encapsulates the business logic for the agentic campaign Review page
 * (the start + end scheduling flow used when a campaign is agentic, regardless of
 * campaign type). Handles form state, validation, schedule restoration, and submission.
 */
export const useAgenticReviewLogic = () => {
  const { t } = useTranslation();
  const campaignId = useSelector(selectCampaignId);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();

  // Redux selectors
  const draftCampaignName = useSelector(selectDraftCampaignName);
  const subscribers = useSelector(selectDraftSubscribers);
  const draftAiAgentData = useSelector(selectDraftAiAgentData);

  // Hooks
  const { campaign, updateCampaignAsync } = useCampaigns(campaignId);
  const { campaign_senders } = useCampaignSenders(campaignId);

  const audienceStats = getAudienceStats(subscribers, campaign?.subscribers_count);

  // Parse senders into chain structure (primary, delivery_failed, no_response chain)
  const { primarySender, deliveryFailedSender, noResponseSenders } =
    parseCampaignSendersChain(campaign_senders);

  // Form for scheduling
  const form = useForm<AgenticReviewSchedulingFormData>({
    defaultValues: {
      startScheduleType: 'immediately',
      startDate: undefined,
      startTime: '',
      timezone: getUserTimezone(),
      doNotEnd: true, // true = hide end date fields (default behavior)
      endDate: undefined,
      endTime: '',
    },
  });

  const { control, setValue, clearErrors, setError } = form;
  const startScheduleType = useWatch({ control, name: 'startScheduleType' });
  const startDate = useWatch({ control, name: 'startDate' });
  const startTime = useWatch({ control, name: 'startTime' });
  const timezone = useWatch({ control, name: 'timezone' });
  const doNotEnd = useWatch({ control, name: 'doNotEnd' });
  const endDate = useWatch({ control, name: 'endDate' });
  const endTime = useWatch({ control, name: 'endTime' });

  // State for confirmation dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Populate form with existing campaign schedule data
  useEffect(() => {
    // Try to restore start date/time from campaign schedule with timezone from metadata
    const restoredSchedule = restoreScheduleFromCampaign(
      campaign?.schedule?.start_at,
      campaign?.metadata?.start_at_tz as string
    );

    if (restoredSchedule) {
      // If we have a restored schedule, it was a custom scheduled campaign
      setValue('startScheduleType', 'custom');
      setValue('startDate', restoredSchedule.selectedDate);
      setValue('startTime', restoredSchedule.selectedTime);
      setValue('timezone', restoredSchedule.selectedTimezone);
    } else if (campaign?.schedule?.start_at && campaign.schedule.start_at !== 'now') {
      // Fallback: if there's a start_at but no timezone metadata, restore using local time
      const startTimestamp = parseInt(campaign.schedule.start_at, 10);
      if (!isNaN(startTimestamp)) {
        const startDateTime = new Date(startTimestamp);
        setValue('startScheduleType', 'custom');
        setValue('startDate', startDateTime);
        setValue(
          'startTime',
          `${String(startDateTime.getHours()).padStart(2, '0')}:${String(startDateTime.getMinutes()).padStart(2, '0')}`
        );
      }
    }
    // If start_at is 'now' or undefined, keep default 'immediately'

    // Restore end date/time from campaign schedule (if exists)
    if (campaign?.schedule?.end_at) {
      const endTimestamp = parseInt(campaign.schedule.end_at, 10);
      if (!isNaN(endTimestamp)) {
        const endDateTime = new Date(endTimestamp);
        setValue('endDate', endDateTime);
        setValue(
          'endTime',
          `${String(endDateTime.getHours()).padStart(2, '0')}:${String(endDateTime.getMinutes()).padStart(2, '0')}`
        );
        setValue('doNotEnd', false);
      }
    }
  }, [campaign?.schedule, campaign?.metadata, setValue]);

  // Handle "Do not end" toggle change
  const handleDoNotEndToggle = useCallback(
    (checked: boolean) => {
      setValue('doNotEnd', checked);
      if (checked) {
        // Clear end date/time when "Do not end" is enabled
        clearErrors(['endDate', 'endTime']);
        setValue('endDate', undefined);
        setValue('endTime', '');
      }
    },
    [setValue, clearErrors]
  );

  // Handle start schedule type change (Immediately vs Custom)
  const handleStartScheduleTypeChange = useCallback(
    (value: 'immediately' | 'custom') => {
      setValue('startScheduleType', value);
      if (value === 'immediately') {
        // Clear start date/time when switching to immediately
        clearErrors(['startDate', 'startTime', 'timezone']);
        setValue('startDate', undefined);
        setValue('startTime', '');
      }
    },
    [setValue, clearErrors]
  );

  // Check if essential data is still loading
  const isLoadingEssentialData = !draftCampaignName || !draftAiAgentData?.brain_parent_id;

  /**
   * Validates the scheduling form before showing confirmation dialog
   */
  const validateSchedulingForm = useCallback(
    (data: AgenticReviewSchedulingFormData) => {
      let isValid = true;

      // Only validate start date/time when schedule type is 'custom'
      if (data.startScheduleType === 'custom') {
        // Validate required start date
        if (!data.startDate) {
          setError('startDate', { type: 'manual', message: t('validation.required') });
          isValid = false;
        }

        // Validate required start time
        if (!data.startTime) {
          setError('startTime', { type: 'manual', message: t('validation.required') });
          isValid = false;
        }

        // Validate required timezone
        if (!data.timezone) {
          setError('timezone', { type: 'manual', message: t('validation.required') });
          isValid = false;
        }

        // Validate start date/time is in the future
        if (data.startDate && data.startTime && data.timezone) {
          if (!isScheduledTimeInFuture(data.startDate, data.startTime, data.timezone)) {
            setError('startTime', {
              type: 'manual',
              message: t('campaigns.paymentrecoveryreview.start_time_future'),
            });
            isValid = false;
          }
        }
      }

      // Validate end fields if "Do not end" is disabled (end date is shown)
      if (!data.doNotEnd) {
        if (!data.endDate) {
          setError('endDate', { type: 'manual', message: t('validation.required') });
          isValid = false;
        }

        if (!data.endTime) {
          setError('endTime', { type: 'manual', message: t('validation.required') });
          isValid = false;
        }

        // Validate end date/time is after start date/time (only when custom schedule)
        if (data.startScheduleType === 'custom') {
          if (data.startDate && data.startTime && data.endDate && data.endTime && data.timezone) {
            const startTimestamp = calculateScheduledTime(
              true,
              data.startDate,
              data.startTime,
              data.timezone
            );
            const endTimestamp = calculateScheduledTime(
              true,
              data.endDate,
              data.endTime,
              data.timezone
            );

            if (
              startTimestamp !== 'now' &&
              endTimestamp !== 'now' &&
              endTimestamp <= startTimestamp
            ) {
              setError('endDate', {
                type: 'manual',
                message: t('campaigns.paymentrecoveryreview.end_after_start'),
              });
              isValid = false;
            }
          }
        }
      }

      return isValid;
    },
    [setError, t]
  );

  /**
   * Common logic for saving campaign data
   * @returns The updated campaign
   */
  const saveCampaignData = useCallback(async (): Promise<Campaign> => {
    // Calculate scheduled start time based on schedule type
    let scheduleStartAt: string = 'now';
    let newMetadata: Record<string, unknown> = {};

    if (startScheduleType === 'custom' && startDate && startTime && timezone) {
      const timestamp = calculateScheduledTime(true, startDate, startTime, timezone);
      if (timestamp !== 'now') {
        scheduleStartAt = timestamp.toString();
        // Save timezone in metadata so we can restore the form later
        newMetadata = { start_at_tz: timezone };
      }
    }

    // Calculate scheduled end time if "Do not end" is disabled
    let scheduleEndAt: string | undefined;
    if (!doNotEnd && endDate && endTime) {
      // Use the form timezone for end date (shared timezone between start and end)
      const endTimestamp = calculateScheduledTime(true, endDate, endTime, timezone);
      if (endTimestamp !== 'now') {
        scheduleEndAt = endTimestamp.toString();
      }
    }

    const finalCampaign = await updateCampaignAsync({
      campaign_id: campaignId,
      schedule: { start_at: scheduleStartAt, ...(scheduleEndAt && { end_at: scheduleEndAt }) },
      // Merge with existing metadata to preserve audience stats from Setup
      metadata: { ...(campaign?.metadata || {}), ...newMetadata },
    });

    return finalCampaign;
  }, [
    campaign?.metadata,
    campaignId,
    startScheduleType,
    startDate,
    startTime,
    timezone,
    doNotEnd,
    endDate,
    endTime,
    updateCampaignAsync,
  ]);

  const handlePrevious = useCallback(() => {
    navigate(`/${slug}/campaigns/${campaignId}/messaging`);
  }, [navigate, slug, campaignId]);

  const handleSaveAndExit = useCallback(async () => {
    try {
      const finalCampaign = await saveCampaignData();
      toast.success(t('campaigns.updated', { 0: finalCampaign.name }));

      // Note: We don't set status to 'ready' here - campaign remains in draft state
      navigate(`/${slug}/campaigns`);
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  }, [saveCampaignData, navigate, slug, t]);

  /**
   * Shows confirmation dialog after validation
   */
  const handleReviewAndStart = useCallback(async (): Promise<boolean> => {
    // Get current form data
    const formData = {
      startScheduleType,
      startDate,
      startTime,
      timezone,
      doNotEnd,
      endDate,
      endTime,
    };

    // Validate scheduling form. Returning a success flag lets callers track only
    // when the confirm dialog actually opens (blocked attempts must not be tracked).
    if (!validateSchedulingForm(formData)) {
      return false;
    }

    // Show confirmation dialog
    setIsConfirmDialogOpen(true);
    return true;
  }, [
    startScheduleType,
    startDate,
    startTime,
    timezone,
    doNotEnd,
    endDate,
    endTime,
    validateSchedulingForm,
  ]);

  /**
   * Actually submits the campaign after confirmation
   */
  const handleConfirmSubmit = useCallback(async () => {
    try {
      setIsConfirmDialogOpen(false);

      const finalCampaign = await saveCampaignData();
      await updateCampaignAsync({ campaign_id: finalCampaign.campaign_id, status: 'ready' });
      trackCampaignLaunched({
        campaign_id: finalCampaign.campaign_id,
        campaign_type: campaign?.type || 'payment_recovery',
        is_scheduled: startScheduleType === 'custom',
      });
      toast.success(t('campaigns.updated', { 0: finalCampaign.name }));

      navigate(`/${slug}/campaigns`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      trackCampaignLaunchFailed(error, {
        campaign_id: campaignId,
        campaign_type: campaign?.type || 'payment_recovery',
        is_scheduled: startScheduleType === 'custom',
      });
      toast.error(t('campaigns.error_creating'));
    }
  }, [
    saveCampaignData,
    updateCampaignAsync,
    navigate,
    slug,
    t,
    campaign?.type,
    campaignId,
    startScheduleType,
  ]);

  return {
    // Form
    form,
    startScheduleType,
    startDate,
    startTime,
    timezone,
    doNotEnd,
    endDate,
    endTime,

    // Dialog state
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,

    // Redux state
    draftCampaignName,
    draftAiAgentData,

    // Derived data
    campaignType: campaign?.type,
    // Only agentic (NBA) campaigns show "additional channels"; non-agentic
    // payment-recovery campaigns still route here but show the delivery/response chain.
    isNBA: !!campaign?.is_agentic,
    audienceStats,
    campaign_senders,
    primarySender,
    deliveryFailedSender,
    noResponseSenders,

    // Loading state
    isLoadingEssentialData,

    // Handlers
    handleDoNotEndToggle,
    handleStartScheduleTypeChange,
    handlePrevious,
    handleSaveAndExit,
    handleReviewAndStart,
    handleConfirmSubmit,
  };
};
